// @workflow assistant.history
import { groupConversations } from '@Assistant/domain/history';
import { showToast, ACTION_TOAST_MS } from '@Assistant/services/toast';
import agentClient from '@Assistant/services/agentClient';

/**
 * The history-list part of the ASSISTANT store (load, delete with Undo), spread into
 * assistant.modules.js. Opening a past conversation stays there: it starts a run.
 */

/** The server caps a page at 50 (MaxConversationsPerPage); the panel shows the newest 30. */
const HISTORY_PAGE_SIZE = 30;

/**
 * Deleted conversations still inside their Undo window: id → timer. The server delete (archive, a
 * soft delete) is sent when the window ends, or straight away if the page is closing.
 */
const pendingDeletes = new Map();

function sendPendingDeletes() {
  pendingDeletes.forEach((timer, id) => {
    clearTimeout(timer);
    agentClient.archiveConversation(id).catch(() => {});
  });
  pendingDeletes.clear();
}
if (typeof window !== 'undefined') window.addEventListener('pagehide', sendPendingDeletes);

export const HISTORY_ACTIONS = Object.freeze({
  /** The signed-in user's own conversations from the server, grouped by day. */
  async LOAD_HISTORY({ commit, state }) {
    commit('SET_HISTORY', { ...state.history, loading: true, error: null });
    try {
      const page = await agentClient.listConversations({ page: 1, itemsPerPage: HISTORY_PAGE_SIZE });
      commit('SET_HISTORY', { ...state.history, groups: groupConversations(page.items, new Date()), loading: false, error: null });
    } catch (err) {
      commit('SET_HISTORY', { ...state.history, loading: false, error: "Couldn't load your conversations" });
    }
  },

  /**
   * Deletes a conversation for this user: it leaves the list at once, with Undo for a few seconds;
   * then the server archives it (soft delete, purged with the history retention). Deleting the
   * open conversation starts a new chat.
   */
  DELETE_CONVERSATION({ commit, state, dispatch }, id) {
    if (!id || pendingDeletes.has(id)) return;
    commit('SET_HISTORY', { ...state.history, deleting: [...state.history.deleting, id] });
    if (state.conversation.id === id || state.activeHistoryId === id) dispatch('NEW_CONVERSATION');
    pendingDeletes.set(id, setTimeout(() => dispatch('FINISH_DELETE', id), ACTION_TOAST_MS));
    showToast('Conversation deleted', { action: { label: 'Undo', run: () => dispatch('UNDO_DELETE', id) } });
  },

  UNDO_DELETE({ commit, state }, id) {
    clearTimeout(pendingDeletes.get(id));
    pendingDeletes.delete(id);
    commit('SET_HISTORY', { ...state.history, deleting: state.history.deleting.filter((d) => d !== id) });
  },

  async FINISH_DELETE({ commit, state, dispatch }, id) {
    if (!pendingDeletes.has(id)) return;
    pendingDeletes.delete(id);
    const stillHidden = () => commit('SET_HISTORY', { ...state.history, deleting: state.history.deleting.filter((d) => d !== id) });
    try {
      await agentClient.archiveConversation(id);
      await dispatch('LOAD_HISTORY');
      stillHidden();
    } catch (err) {
      stillHidden();
      showToast("Couldn't delete this conversation");
    }
  },
});
